import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { NATIVE_APP_CLIDS } from '@/constants/general'
import type { AdType, OutOfPageAdType } from '@/modules/adv/types'
import type { ChargeResponse, DisableAdOrder } from '@/modules/in-game-payments'
import { useNativeExactPackPurchase } from '@/modules/native-payments/native-exact-pack-purchase'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import { CLIENT_QUERY } from '@/router/query-params'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { useClientAppStore } from '@/store/client-app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { getConfigByClid } from '@/utils/clid-config'

export const useAdDisabledStore = defineStore('ad-disabled', () => {
    const appStore = useAppStore()
    const clientAppStore = useClientAppStore()
    const adsStore = useAdsStore()
    const generalData = useGeneralData()
    const userStore = useUserStore()
    const layoutStore = useLayoutStore()
    const loggerStore = useLoggerStore()
    const { chargeUser: chargeUserWithNativeExactPack } = useNativeExactPackPurchase(layoutStore.setPaymentModalOrder)
    const pricesConfig = computed(() => generalData.mainData.value.noAdsConfigInGam)
    const { user } = storeToRefs(userStore)
    const { isPortalEnv, initialQuery, clid, isExportOnWidget } = storeToRefs(appStore)
    const clidConfig = computed(() => getConfigByClid(clid.value))
    const route = useRoute()
    const isOutOfPlaygama = computed(() => !isPortalEnv.value && !isExportOnWidget.value)
    const isExportPage = computed(() => route.name === ROUTE_NAMES.EXPORT_GAME_PAGE)

    const isAdDisabled = computed(() => {
        // special queries for export page
        if (isExportPage.value && !!initialQuery.value?.[CLIENT_QUERY.USE_NO_ADS]) {
            return true
        }
        // Native TWA clid
        if (clid.value === NATIVE_APP_CLIDS.twa) {
            return true
        }

        // Partner clids with adDisabled flag (TWA partners, Xiaomi test, etc.)
        if (clidConfig.value.adDisabled && isOutOfPlaygama.value && isExportPage.value) {
            return true
        }
        return isAdFreeActive.value
    })

    const isAdFreeActive = ref(false)
    const remainingMsWithoutAd = ref(0)

    function checkAdDisabled() {
        const until = user.value?.adsDisabledUntil
        const timeRemainingMs = isPortalEnv.value && until ? new Date(until).getTime() - Date.now() : 0
        isAdFreeActive.value = timeRemainingMs > 0
        adsStore.updateTargeting({
            pg_ad_disabled: isAdDisabled.value.toString(),
        })
        remainingMsWithoutAd.value = isAdFreeActive.value ? timeRemainingMs : 0
    }

    if (typeof window !== 'undefined') {
        checkAdDisabled()
        setInterval(checkAdDisabled, 60_000)
        watch(() => user.value?.adsDisabledUntil, checkAdDisabled)
        watch(
            () => isAdDisabled.value,
            () => adsStore.adService?.shutdown(),
        )
    }

    const adDisabledModalOptions = ref({
        isOpened: false,
        isTopUp: false,
        placement: undefined as AdType | undefined,
    })
    const isAdFinalScreenVisible = ref(false)
    const resolveModalFn = ref<((value: ChargeResponse<DisableAdOrder> | null) => void) | null>(null)

    function closeAdDisabledModal() {
        // if the user closed the modal without purchasing, we still check active subscription
        const adDisabledResolverResult = isAdFreeActive.value
            ? {
                  data: { status: 'PAID' as const, orderId: '', amount: 0 },
                  errors: [],
                  originalResponse: null as unknown as Response,
              }
            : null
        resolveModalFn.value?.(adDisabledResolverResult)
        resolveModalFn.value = null
        adDisabledModalOptions.value = {
            isOpened: false,
            isTopUp: false,
            placement: undefined,
        }
    }

    function closeNoAdFinalScreen() {
        isAdFinalScreenVisible.value = false
    }

    async function openDisabledAdModal(placement?: OutOfPageAdType): Promise<ChargeResponse<DisableAdOrder> | null> {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'open_purchase_window',
            adPlacement: placement,
        })

        try {
            const currentUser = await userStore.authorizeUser('disable-ad-modal')

            if (currentUser) {
                adDisabledModalOptions.value = {
                    isOpened: true,
                    isTopUp: isAdFreeActive.value,
                    placement,
                }
                return new Promise<ChargeResponse<DisableAdOrder> | null>((resolve) => {
                    resolveModalFn.value = resolve
                })
            }
        } catch {}

        return null
    }

    async function disableAd(durationInDays: 1 | 7 | 30) {
        adDisabledModalOptions.value.isOpened = false

        try {
            const result = await chargeUserWithNativeExactPack({
                type: 'disable-ad-order',
                amount: pricesConfig.value.basic[durationInDays],
                durationInDays,
                attributes: {
                    clid: appStore.clid,
                    widgetId: clientAppStore.widgetId,
                    userAcquisitionParams: clientAppStore.userAcquisitionParams,
                },
                externalId: `no-ads-${durationInDays}-days-${Date.now()}`,
                placement: adDisabledModalOptions.value.placement,
                subscription: 'basic-days',
                isTopUp: adDisabledModalOptions.value.isTopUp,
            })
            resolveModalFn.value?.((result as ChargeResponse<DisableAdOrder>) ?? null)
            resolveModalFn.value = null
            isAdFinalScreenVisible.value = true
        } catch {
            resolveModalFn.value?.(null)
            resolveModalFn.value = null
        }

        adDisabledModalOptions.value.placement = undefined
    }

    return {
        isAdDisabled,
        isAdFreeActive,
        remainingMsWithoutAd,
        disableAd,
        adDisabledModalOptions,
        openDisabledAdModal,
        closeAdDisabledModal,
        isAdFinalScreenVisible,
        closeNoAdFinalScreen,
        checkAdDisabled,
        pricesConfig,
    }
})
